import "./proxyHealth.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconCloud,
  IconCopy,
  IconDatabase,
  IconHistory,
  IconPlayerPlay,
  IconRouter,
  IconWifi,
} from "@tabler/icons-react";
import moment from "moment";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pill,
  RefreshButton,
  SkeletonRows,
  SkeletonStats,
  Stat,
  Tone as UiTone,
} from "../ui";
import {
  ProxyHealthFailedStart,
  ProxyHealthGatewayRow,
  ProxyHealthPayload,
  ProxyHealthStuckCommand,
} from "./proxyHealthTypes";
import StuckCommandDetailModal, {
  stuckAttemptsTooltip,
} from "./StuckCommandDetailModal";

const STUCK_SECTION_ID = "proxy-health-stuck-commands";

const POLL_MS = 30_000;
/** Alliance hard cap — matches sqi-proxy RateLimiter / published docs. */
const ALLIANCE_RPS_CAP = 10;
/** Background traffic reserves this many slots; warn when remaining ≤ this. */
const BACKGROUND_RESERVE = 2;
/** Flag the payload as stale once it's this many polls old (backend/network hiccup). */
const STALE_AFTER_MS = POLL_MS * 2.5;
/** Rolling client-side trend buffer — resets on page load, not persisted. */
const MAX_HISTORY = 40;

type HealthTone = "ok" | "warn" | "critical" | "unknown";

interface HistoryPoint {
  t: number;
  remaining: number | null;
  open: number;
  stuck: number;
  failedStartsLast1h: number;
  requestsTotal: number;
  responses429: number;
  retriesTotal: number;
}

interface Issue {
  severity: "warn" | "critical";
  label: string;
  scrollToId?: string;
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function formatGeneratedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.fromNow() : iso;
}

function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : iso;
}

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0s";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCountdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${s}s`;
}

function rateTone(remaining: number | null): HealthTone {
  if (remaining == null) return "unknown";
  if (remaining <= 0) return "critical";
  if (remaining <= BACKGROUND_RESERVE) return "warn";
  return "ok";
}

/** Max non-null releaseVersion across the gateway list (fleet baseline). */
function fleetMaxReleaseVersion(
  gateways: ProxyHealthGatewayRow[] | undefined
): number | null {
  if (!gateways?.length) return null;
  let max: number | null = null;
  for (const g of gateways) {
    if (g.releaseVersion == null || !Number.isFinite(g.releaseVersion)) continue;
    if (max == null || g.releaseVersion > max) max = g.releaseVersion;
  }
  return max;
}

function isGatewayOutdated(
  releaseVersion: number | null | undefined,
  maxRelease: number | null
): boolean {
  return (
    maxRelease != null &&
    releaseVersion != null &&
    Number.isFinite(releaseVersion) &&
    releaseVersion < maxRelease
  );
}

function gatewayStatusRank(status: string): number {
  const s = status.toLowerCase();
  if (s === "offline") return 0;
  if (s === "online") return 2;
  return 1;
}

/** Offline first, then outdated, then other; stable by location/name within groups. */
function sortGatewaysForDisplay(
  gateways: ProxyHealthGatewayRow[],
  maxRelease: number | null
): ProxyHealthGatewayRow[] {
  return [...gateways].sort((a, b) => {
    const statusCmp =
      gatewayStatusRank(a.gatewayStatus) - gatewayStatusRank(b.gatewayStatus);
    if (statusCmp !== 0) return statusCmp;
    const aOut = isGatewayOutdated(a.releaseVersion, maxRelease) ? 0 : 1;
    const bOut = isGatewayOutdated(b.releaseVersion, maxRelease) ? 0 : 1;
    if (aOut !== bOut) return aOut - bOut;
    const locA = (a.locationName || a.locationApiId).toLowerCase();
    const locB = (b.locationName || b.locationApiId).toLowerCase();
    if (locA !== locB) return locA < locB ? -1 : 1;
    return a.gatewayName.localeCompare(b.gatewayName);
  });
}

function toneLabel(tone: HealthTone): string {
  switch (tone) {
    case "ok":
      return "OK";
    case "warn":
      return "Warn";
    case "critical":
      return "Critical";
    default:
      return "Unknown";
  }
}

const UI_TONE: Record<HealthTone, UiTone> = {
  ok: "good",
  warn: "warn",
  critical: "bad",
  unknown: "neutral",
};

/** undefined until two samples exist; null if the counter reset (proxy restart). */
function sinceLast(hist: HistoryPoint[], key: keyof HistoryPoint): number | null | undefined {
  if (hist.length < 2) return undefined;
  const curr = hist[hist.length - 1][key] as number;
  const prev = hist[hist.length - 2][key] as number;
  const d = curr - prev;
  return d >= 0 ? d : null;
}

function Delta({ value }: { value: number | null | undefined }) {
  if (!value) return null;
  return <span className="proxy-delta">+{value} since last check</span>;
}

function Sparkline({ values, tone }: { values: Array<number | null>; tone: HealthTone }) {
  const clean = values.filter((v): v is number => v != null);
  if (clean.length < 2) return null;
  const width = 100;
  const height = 26;
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const stepX = width / (clean.length - 1);
  const coords = clean.map((v, i) => ({
    x: i * stepX,
    y: height - ((v - min) / range) * (height - 6) - 3,
  }));
  const points = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  return (
    <svg
      className={`proxy-spark tone-${UI_TONE[tone]}`}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline points={points} fill="none" strokeWidth={1.75} />
    </svg>
  );
}

function StripCard({
  tone,
  icon,
  label,
  value,
  unit,
  note,
  history,
  onClick,
}: {
  tone: HealthTone;
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  unit?: React.ReactNode;
  note: React.ReactNode;
  history: Array<number | null>;
  onClick?: () => void;
}) {
  return (
    <Stat
      tone={tone === "ok" || tone === "unknown" ? undefined : UI_TONE[tone]}
      icon={icon}
      label={label}
      value={value}
      unit={unit}
      note={note}
      onClick={onClick}
      aside={<Pill tone={UI_TONE[tone]}>{toneLabel(tone)}</Pill>}
      footer={<Sparkline values={history} tone={tone} />}
    />
  );
}

function computeIssues(proxy: ProxyHealthPayload["proxy"], tone: HealthTone, stale: boolean): Issue[] {
  const issues: Issue[] = [];
  if (proxy.db !== "ok") {
    issues.push({ severity: "critical", label: "Database error" });
  }
  if (!proxy.alliance.ok) {
    issues.push({
      severity: "critical",
      label: `Alliance API degraded${proxy.alliance.error ? ` — ${proxy.alliance.error}` : ""}`,
    });
  }
  if (proxy.commands.stuck > 0) {
    issues.push({
      severity: "critical",
      label: `${proxy.commands.stuck} stuck command${proxy.commands.stuck === 1 ? "" : "s"}`,
      scrollToId: STUCK_SECTION_ID,
    });
  }
  if (tone === "critical") {
    issues.push({ severity: "critical", label: "Alliance rate limit exhausted" });
  } else if (tone === "warn") {
    issues.push({ severity: "warn", label: "Alliance rate limit low" });
  }
  if (proxy.ingest.enabled && !proxy.ingest.wsConnected) {
    issues.push({ severity: "critical", label: "Ingest WebSocket down" });
  }
  if (proxy.commands.failedStartsLast1h > 0) {
    issues.push({
      severity: "warn",
      label: `${proxy.commands.failedStartsLast1h} failed start${
        proxy.commands.failedStartsLast1h === 1 ? "" : "s"
      } in the last hour`,
    });
  }
  const gwOffline = proxy.gateways?.offline ?? 0;
  if (gwOffline > 0) {
    issues.push({
      severity: gwOffline >= 3 ? "critical" : "warn",
      label: `${gwOffline} gateway${gwOffline === 1 ? "" : "s"} offline`,
    });
  }
  const maxRelease = fleetMaxReleaseVersion(proxy.gateways?.gateways);
  if (maxRelease != null) {
    const outdatedCount = (proxy.gateways?.gateways ?? []).filter((g) =>
      isGatewayOutdated(g.releaseVersion, maxRelease)
    ).length;
    if (outdatedCount > 0) {
      issues.push({
        severity: "warn",
        label: `${outdatedCount} gateway${
          outdatedCount === 1 ? "" : "s"
        } behind r${maxRelease}`,
      });
    }
  }
  if (proxy.gateways?.lastError) {
    issues.push({
      severity: "warn",
      label: `Gateway sweep issue — ${proxy.gateways.lastError}`,
    });
  }
  if (stale) {
    issues.push({ severity: "warn", label: "Health data may be stale" });
  }
  return issues;
}

export default function ProxyHealth() {
  const [data, setData] = useState<ProxyHealthPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [tabVisible, setTabVisible] = useState(true);
  const [selectedStuck, setSelectedStuck] = useState<ProxyHealthStuckCommand | null>(null);
  const visibleRef = useRef(true);
  const historyRef = useRef<HistoryPoint[]>([]);
  const lastLoadAtRef = useRef<number>(Date.now());

  const load = useCallback(async (opts?: { soft?: boolean }) => {
    if (opts?.soft) setRefreshing(true);
    else setLoading(true);
    setError(null);
    lastLoadAtRef.current = Date.now();
    try {
      const res = await axiosInstance.get<ProxyHealthPayload>("/ops/proxy-health");
      setData(res.data);
      const p = res.data.proxy;
      historyRef.current = [
        ...historyRef.current,
        {
          t: Date.now(),
          remaining: p.alliance.rateLimit.remaining,
          open: p.commands.open,
          stuck: p.commands.stuck,
          failedStartsLast1h: p.commands.failedStartsLast1h,
          requestsTotal: p.alliance.stats.requestsTotal,
          responses429: p.alliance.stats.responses429,
          retriesTotal: p.alliance.stats.retriesTotal,
        },
      ].slice(-MAX_HISTORY);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to load proxy health.";
      setError(msg);
      if (!opts?.soft) setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVisibility = () => {
      const visible = document.visibilityState === "visible";
      visibleRef.current = visible;
      setTabVisible(visible);
    };
    document.addEventListener("visibilitychange", onVisibility);
    const id = window.setInterval(() => {
      if (visibleRef.current) load({ soft: true });
    }, POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(id);
    };
  }, [load]);

  // Drives relative-time labels, the staleness check and the refresh countdown.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (visibleRef.current) setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const copyDiagnostics = useCallback(() => {
    if (!data || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }, [data]);

  const proxy = data?.proxy;
  const remaining = proxy?.alliance.rateLimit.remaining ?? null;
  const tone = rateTone(remaining);
  const stuckThresholdMin = proxy ? Math.round(proxy.commands.stuckThresholdMs / 60000) : 2;

  const ageMs = data ? now - new Date(data.generatedAt).getTime() : 0;
  const stale = !!data && Number.isFinite(ageMs) && ageMs > STALE_AFTER_MS;

  const issues = useMemo(() => (proxy ? computeIssues(proxy, tone, stale) : []), [
    proxy,
    tone,
    stale,
  ]);
  const overall: HealthTone = issues.some((i) => i.severity === "critical")
    ? "critical"
    : issues.some((i) => i.severity === "warn")
      ? "warn"
      : proxy
        ? "ok"
        : "unknown";

  const maxReleaseVersion = useMemo(
    () => fleetMaxReleaseVersion(proxy?.gateways?.gateways),
    [proxy?.gateways?.gateways]
  );
  const sortedGateways = useMemo(() => {
    const rows = proxy?.gateways?.gateways;
    if (!rows?.length) return [];
    return sortGatewaysForDisplay(rows, maxReleaseVersion);
  }, [proxy?.gateways?.gateways, maxReleaseVersion]);

  const locationNameByApiId = useMemo(() => {
    const map = new Map<string, string>();
    for (const gw of proxy?.gateways?.gateways ?? []) {
      if (gw.locationApiId && gw.locationName && !map.has(gw.locationApiId)) {
        map.set(gw.locationApiId, gw.locationName);
      }
    }
    return map;
  }, [proxy?.gateways?.gateways]);

  const stuckCommands = proxy?.commands.stuckCommands ?? [];

  const countdownMs = Math.max(0, POLL_MS - (now - lastLoadAtRef.current));
  const history = historyRef.current;
  const requestsDelta = sinceLast(history, "requestsTotal");
  const responses429Delta = sinceLast(history, "responses429");
  const retriesDelta = sinceLast(history, "retriesTotal");
  const gatewaysOffline = proxy?.gateways?.offline ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title="Alliance / proxy health"
        subtitle={`Shared ${ALLIANCE_RPS_CAP} req/s Alliance budget via sqi-proxy.${
          data?.generatedAt ? ` Updated ${formatGeneratedAt(data.generatedAt)}` : ""
        }${refreshing ? " · refreshing…" : ""}`}
        actions={
          <>
            <Link to="/proxy-health/commands" className="btn btn-sm btn-outline-secondary">
              <IconHistory size={16} />
              Command history
            </Link>
            {data ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={copyDiagnostics}
                title="Copy the full health payload as JSON"
              >
                <IconCopy size={16} />
                {copied ? "Copied!" : "Copy diagnostics"}
              </button>
            ) : null}
            <RefreshButton onClick={() => load({ soft: true })} loading={loading || refreshing} />
          </>
        }
      />

      {error ? <ErrorBanner onRetry={() => load()}>{error}</ErrorBanner> : null}

      {loading && !data ? (
        <div className="sq-stack" role="status" aria-label="Loading proxy health">
          <SkeletonStats count={5} />
          <Card>
            <SkeletonRows rows={5} />
          </Card>
        </div>
      ) : null}

      {proxy ? (
        <div className="sq-stack">
          <section className={`sq-hero tone-${UI_TONE[overall]}`} role="status" aria-live="polite">
            <div className="sq-hero-main">
              <span className="sq-hero-icon" aria-hidden="true">
                {overall === "ok" ? (
                  <IconCircleCheck size={26} />
                ) : overall === "critical" ? (
                  <IconCircleX size={26} />
                ) : (
                  <IconAlertTriangle size={26} />
                )}
              </span>
              <div>
                <p className="sq-hero-title">
                  {overall === "ok"
                    ? "All systems normal"
                    : overall === "critical"
                      ? "Critical issues detected"
                      : "Degraded"}
                </p>
                <p className="sq-hero-sub">
                  Checked {formatAgo(data?.generatedAt)}
                  {refreshing ? " · refreshing…" : ""}
                </p>
              </div>
            </div>
            <div className="sq-hero-side">
              {issues.length > 0 ? (
                <ul className="sq-hero-issues">
                  {issues.map((issue, i) => (
                    <li key={i}>
                      <Pill tone={issue.severity === "critical" ? "bad" : "warn"}>
                        {issue.scrollToId ? (
                          <button type="button" onClick={() => scrollToSection(issue.scrollToId!)}>
                            {issue.label}
                          </button>
                        ) : (
                          issue.label
                        )}
                      </Pill>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="sq-hero-sub">Nothing needs attention right now.</span>
              )}
              <span className="sq-hero-countdown">
                <IconClock size={13} />
                {tabVisible ? `Next check in ${formatCountdown(countdownMs)}` : "Paused (tab hidden)"}
              </span>
            </div>
          </section>

          <section className="sq-stats" aria-label="Rate limit and commands">
            <StripCard
              tone={tone}
              icon={<IconCloud size={16} />}
              label="Rate limit left"
              value={remaining != null ? remaining : "—"}
              unit={
                proxy.alliance.rateLimit.limit != null
                  ? `/ ${proxy.alliance.rateLimit.limit}`
                  : `(cap ${ALLIANCE_RPS_CAP}/s)`
              }
              note={
                <>
                  Reset {formatAgo(proxy.alliance.rateLimit.resetAt)}
                  {tone === "warn" || tone === "critical"
                    ? " · background traffic paused when ≤2"
                    : ""}
                </>
              }
              history={history.map((h) => h.remaining)}
            />
            <StripCard
              tone={proxy.commands.open > 0 ? "warn" : "ok"}
              icon={<IconPlayerPlay size={16} />}
              label="Open commands"
              value={proxy.commands.open}
              note="PENDING / QUEUED on ledger"
              history={history.map((h) => h.open)}
            />
            <StripCard
              tone={proxy.commands.stuck > 0 ? "critical" : "ok"}
              icon={<IconAlertTriangle size={16} />}
              label="Stuck commands"
              value={proxy.commands.stuck}
              note={`Open longer than ${stuckThresholdMin} min`}
              history={history.map((h) => h.stuck)}
              onClick={
                proxy.commands.stuck > 0 ? () => scrollToSection(STUCK_SECTION_ID) : undefined
              }
            />
            <StripCard
              tone={proxy.commands.failedStartsLast1h > 0 ? "warn" : "ok"}
              icon={<IconAlertTriangle size={16} />}
              label="Failed starts (1h)"
              value={proxy.commands.failedStartsLast1h}
              note="START / START_DRYER_WITH_TIME"
              history={history.map((h) => h.failedStartsLast1h)}
            />
            <StripCard
              tone={gatewaysOffline > 0 ? (gatewaysOffline >= 3 ? "critical" : "warn") : "ok"}
              icon={<IconRouter size={16} />}
              label="Gateways online"
              value={`${proxy.gateways?.online ?? 0} / ${proxy.gateways?.total ?? 0}`}
              note={
                gatewaysOffline > 0
                  ? `${proxy.gateways.offline} offline · last sweep ${formatAgo(
                      proxy.gateways.lastSweepAt
                    )}`
                  : `Last sweep ${formatAgo(proxy.gateways?.lastSweepAt)}`
              }
              history={[]}
            />
          </section>

          <Card
            flush
            title={
              <>
                <IconCloud size={18} />
                Alliance API
              </>
            }
            subtitle={`Process-wide limiter; interactive starts share the budget with command polling and hourly sweeps. Counters are cumulative since proxy start (${formatUptime(
              proxy.uptimeSeconds
            )} ago).`}
            aria-label="Alliance API"
          >
            <dl className="sq-dl">
              <div>
                <dt>Status</dt>
                <dd className={proxy.alliance.ok ? "sq-value-good" : "sq-value-bad"}>
                  {proxy.alliance.ok ? "OK" : "Degraded"}
                  {!proxy.alliance.ok && proxy.alliance.error ? ` — ${proxy.alliance.error}` : ""}
                </dd>
              </div>
              <div>
                <dt>Last success</dt>
                <dd title={formatAbsolute(proxy.alliance.lastSuccessAt)}>
                  {formatAgo(proxy.alliance.lastSuccessAt)}
                </dd>
              </div>
              <div>
                <dt>Requests (process)</dt>
                <dd>
                  {proxy.alliance.stats.requestsTotal}
                  <Delta value={requestsDelta} />
                </dd>
              </div>
              <div>
                <dt>HTTP 429s</dt>
                <dd className={proxy.alliance.stats.responses429 > 0 ? "sq-value-warn" : undefined}>
                  {proxy.alliance.stats.responses429}
                  {proxy.alliance.stats.last429At
                    ? ` (last ${formatAgo(proxy.alliance.stats.last429At)})`
                    : ""}
                  <Delta value={responses429Delta} />
                </dd>
              </div>
              <div>
                <dt>Retries</dt>
                <dd>
                  {proxy.alliance.stats.retriesTotal}
                  <Delta value={retriesDelta} />
                </dd>
              </div>
              <div>
                <dt>Throttle wait total</dt>
                <dd>{formatMs(proxy.alliance.stats.throttleWaitMsTotal)}</dd>
              </div>
            </dl>
          </Card>

          <Card
            flush
            title={
              <>
                <IconDatabase size={18} />
                Proxy process
              </>
            }
            aria-label="Proxy process"
          >
            <dl className="sq-dl">
              <div>
                <dt>
                  <IconDatabase size={14} />
                  Database
                </dt>
                <dd className={proxy.db === "ok" ? "sq-value-good" : "sq-value-bad"}>
                  {proxy.db === "ok" ? "OK" : "Error"}
                </dd>
              </div>
              <div>
                <dt>Uptime</dt>
                <dd>{formatUptime(proxy.uptimeSeconds)}</dd>
              </div>
              <div>
                <dt>
                  <IconWifi size={14} />
                  Ingest
                </dt>
                <dd
                  className={
                    proxy.ingest.enabled && !proxy.ingest.wsConnected ? "sq-value-bad" : undefined
                  }
                >
                  {!proxy.ingest.enabled
                    ? "Disabled"
                    : proxy.ingest.wsConnected
                      ? "Connected"
                      : "Enabled, WS down"}
                  {proxy.ingest.enabled
                    ? ` · ${proxy.ingest.subscribedLocations}/${proxy.ingest.knownLocations} locations`
                    : ""}
                </dd>
              </div>
              <div>
                <dt>Last status event</dt>
                <dd title={formatAbsolute(proxy.ingest.lastStatusAt)}>
                  {formatAgo(proxy.ingest.lastStatusAt)}
                </dd>
              </div>
              <div>
                <dt>Last WS connected</dt>
                <dd title={formatAbsolute(proxy.ingest.lastWsConnectedAt)}>
                  {formatAgo(proxy.ingest.lastWsConnectedAt)}
                </dd>
              </div>
              <div>
                <dt>Last bootstrap</dt>
                <dd title={formatAbsolute(proxy.ingest.lastBootstrapAt)}>
                  {formatAgo(proxy.ingest.lastBootstrapAt)}
                </dd>
              </div>
              <div>
                <dt>Last error WS</dt>
                <dd title={formatAbsolute(proxy.ingest.lastErrorWsAt)}>
                  {formatAgo(proxy.ingest.lastErrorWsAt)}
                </dd>
              </div>
              <div>
                <dt>Last error sweep</dt>
                <dd title={formatAbsolute(proxy.ingest.lastErrorSweepAt)}>
                  {formatAgo(proxy.ingest.lastErrorSweepAt)}
                </dd>
              </div>
              {proxy.ingest.lastError ? (
                <div className="is-wide">
                  <dt>Ingest error</dt>
                  <dd className="sq-value-warn">{proxy.ingest.lastError}</dd>
                </div>
              ) : null}
            </dl>
          </Card>

          <Card
            flush
            title={
              <>
                <IconRouter size={18} />
                Site gateways
              </>
            }
            subtitle="Alliance network gateways per location. Offline gateways usually mean machines at that site lose connectivity."
            aria-label="Site gateways"
          >
            {!proxy.gateways || proxy.gateways.gateways.length === 0 ? (
              <EmptyState compact title="No gateway data yet">
                {proxy.gateways?.lastError || "Waiting for the first sweep."}
              </EmptyState>
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col">Status</th>
                      <th scope="col">Gateway</th>
                      <th scope="col">Location</th>
                      <th scope="col">Last Alliance update</th>
                      <th scope="col">Checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedGateways.map((row: ProxyHealthGatewayRow) => {
                      const status = String(row.gatewayStatus || "").toLowerCase();
                      const offline = status === "offline";
                      const outdated = isGatewayOutdated(row.releaseVersion, maxReleaseVersion);
                      const metaParts: string[] = [];
                      if (row.antennaType) metaParts.push(row.antennaType);
                      if (row.releaseVersion != null) {
                        metaParts.push(`r${row.releaseVersion}`);
                      }
                      return (
                        <tr
                          key={`${row.locationApiId}:${row.gatewayName}`}
                          className={offline ? "is-danger" : undefined}
                        >
                          <td data-label="Status">
                            <Pill
                              tone={status === "online" ? "good" : offline ? "bad" : "neutral"}
                            >
                              {status || "unknown"}
                            </Pill>
                          </td>
                          <td className="cell-title">
                            <div className="sq-strong">{row.gatewayName}</div>
                            {metaParts.length > 0 || outdated ? (
                              <div className="sq-muted d-flex align-items-center gap-1">
                                {metaParts.join(" · ")}
                                {outdated && maxReleaseVersion != null ? (
                                  <Pill
                                    tone="warn"
                                    icon={<IconAlertTriangle size={12} />}
                                    title={`Behind fleet max r${maxReleaseVersion}`}
                                  >
                                    Behind r{maxReleaseVersion}
                                  </Pill>
                                ) : null}
                              </div>
                            ) : null}
                          </td>
                          <td data-label="Location">{row.locationName || row.locationApiId}</td>
                          <td data-label="Last update" title={formatAbsolute(row.latestStatusUpdate)}>
                            {formatAgo(row.latestStatusUpdate)}
                          </td>
                          <td data-label="Checked" title={formatAbsolute(row.checkedAt)}>
                            {formatAgo(row.checkedAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card
            flush
            id={STUCK_SECTION_ID}
            title={
              <>
                <IconAlertTriangle size={18} />
                Stuck commands
              </>
            }
            subtitle={`PENDING / QUEUED ledger rows older than ${stuckThresholdMin} min. Auto-fail runs after ~5 min.`}
            actions={
              <Link to="/proxy-health/commands?status=PENDING" className="sq-link">
                View in history
              </Link>
            }
            aria-label="Stuck commands"
          >
            {stuckCommands.length === 0 ? (
              <EmptyState compact icon={<IconCircleCheck size={22} />} title="No stuck commands" />
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col">Command</th>
                      <th scope="col">Age</th>
                      <th scope="col">Status</th>
                      <th scope="col">Machine</th>
                      <th scope="col">Location</th>
                      <th scope="col">Attempts</th>
                      <th scope="col">Alliance id</th>
                      <th scope="col">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stuckCommands.map((row: ProxyHealthStuckCommand) => {
                      const locationName = locationNameByApiId.get(row.locationApiId) ?? null;
                      return (
                        <tr key={row.id}>
                          <td className="cell-title">
                            <span className="sq-mono">{row.command}</span>
                          </td>
                          <td data-label="Age" title={formatAbsolute(row.createdAt)}>
                            {formatAgo(row.createdAt)}
                          </td>
                          <td data-label="Status">
                            <Pill tone="warn">{row.status}</Pill>
                          </td>
                          <td data-label="Machine">
                            <span className="sq-mono">{row.macId}</span>
                          </td>
                          <td data-label="Location">
                            <div>{locationName || row.locationApiId}</div>
                            {locationName ? (
                              <div className="sq-mono">{row.locationApiId}</div>
                            ) : null}
                          </td>
                          <td data-label="Attempts">
                            <button
                              type="button"
                              className="proxy-attempts"
                              title={stuckAttemptsTooltip(row)}
                              aria-label={`${row.attempts} attempts, show details`}
                              onClick={() => setSelectedStuck(row)}
                            >
                              {row.attempts}
                            </button>
                          </td>
                          <td data-label="Alliance id">
                            {row.allianceCommandId ? (
                              <span className="sq-mono">{row.allianceCommandId}</span>
                            ) : (
                              <span className="sq-muted">Not dispatched</span>
                            )}
                          </td>
                          <td
                            data-label="Error"
                            className="truncate cell-wide"
                            title={row.lastError || undefined}
                          >
                            {row.lastError || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card
            flush
            title={
              <>
                <IconAlertTriangle size={18} />
                Recent failed starts
              </>
            }
            subtitle="Latest failed START / START_DRYER_WITH_TIME rows from the command ledger."
            actions={
              <Link to="/proxy-health/commands?status=FAILED&command=START" className="sq-link">
                View in history
              </Link>
            }
            aria-label="Recent failed starts"
          >
            {proxy.commands.recentFailedStarts.length === 0 ? (
              <EmptyState compact icon={<IconCircleCheck size={22} />} title="No failed starts recorded" />
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col">Command</th>
                      <th scope="col">When</th>
                      <th scope="col">Machine</th>
                      <th scope="col">Location</th>
                      <th scope="col">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proxy.commands.recentFailedStarts.map((row: ProxyHealthFailedStart) => {
                      const recent = Date.now() - new Date(row.createdAt).getTime() < 5 * 60 * 1000;
                      return (
                        <tr key={row.id} className={recent ? "is-flagged" : undefined}>
                          <td className="cell-title">
                            <span className="sq-mono">{row.command}</span>
                          </td>
                          <td data-label="When" title={formatAbsolute(row.createdAt)}>
                            {formatAgo(row.createdAt)}
                          </td>
                          <td data-label="Machine">
                            <span className="sq-mono">{row.macId}</span>
                          </td>
                          <td data-label="Location">
                            <span className="sq-mono">{row.locationApiId}</span>
                          </td>
                          <td
                            data-label="Error"
                            className="truncate cell-wide"
                            title={row.lastError || undefined}
                          >
                            {row.lastError || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <StuckCommandDetailModal
            open={selectedStuck != null}
            onClose={() => setSelectedStuck(null)}
            command={selectedStuck}
            locationName={
              selectedStuck ? locationNameByApiId.get(selectedStuck.locationApiId) ?? null : null
            }
          />
        </div>
      ) : null}
    </PageContainer>
  );
}
